import { makeAutoObservable } from 'mobx'
import { ReCaptchaV2Store } from 'entities/Auth/ui/ReCaptcha/ui/ReCaptchaV2'
import { ReCaptchaV3Store } from 'entities/Auth/ui/ReCaptcha/ui/ReCaptchaV3'
import { CAPTCHA_RESPONSE_CODES } from 'entities/Auth/lib/captchaErrors'
import type { IParamsGoogleRecaptcha } from 'entities/Auth/store/types'

export class ReCaptchaStore {
  private _reCaptchaV2Store = new ReCaptchaV2Store()
  private _reCaptchaV3Store = new ReCaptchaV3Store()
  private _lastAction = ''

  constructor() {
    makeAutoObservable(this)
  }

  get reCaptchaV2Store() {
    return this._reCaptchaV2Store
  }

  get reCaptchaV3Store() {
    return this._reCaptchaV3Store
  }

  get lastAction() {
    return this._lastAction
  }

  get visualChallengeToken() {
    return this._reCaptchaV2Store.token
  }

  get captchaToken() {
    return this._reCaptchaV3Store.token
  }

  setExecuteRecaptcha = async (action: string) => {
    this._lastAction = action
    this._reCaptchaV2Store.setError('')
    this._reCaptchaV2Store.setToken('')

    return await this._reCaptchaV3Store.initToken(action)
  }

  /**
   * Re-mints a v3 token for the action of the rejected request. The strict routes make a v3 token
   * single-use, so `captcha_token_invalid` is retryable — but only with a token minted for the
   * same action, since the backend matches the action per route.
   *
   * The action is passed in rather than read from `_lastAction`: this store is a singleton shared
   * by login, forgot and register, so by the time a response is handled another flow may have
   * moved it on. The caller snapshots the action onto the request config at send time.
   */
  refreshCaptchaToken = async (action?: string) => {
    const target = action || this._lastAction

    if (!target) return ''

    return await this.setExecuteRecaptcha(target)
  }

  setVisualChallenge = async () => {
    return await this._reCaptchaV2Store.initToken()
  }

  checkServerError = (message: string) => {
    return message === CAPTCHA_RESPONSE_CODES.visualChallengeNeeded
  }

  get disabled() {
    return !this.visualChallengeToken && !this.captchaToken
  }

  get params(): IParamsGoogleRecaptcha {
    if (this.captchaToken && this.visualChallengeToken) {
      return {
        captcha_token: this.captchaToken,
        visual_challenge_token: this.visualChallengeToken,
      }
    }

    if (this.visualChallengeToken) {
      return {
        visual_challenge_token: this.visualChallengeToken,
      }
    }

    return {
      captcha_token: this.captchaToken || '',
    }
  }
}
